import { motion } from 'framer-motion';
import { Award, Truck as TruckIcon, Flag, Heart } from 'lucide-react';

const FACTS = [
  { value: '1944', label: 'Convoy Born', icon: Flag },
  { value: '6,000+', label: 'Trucks Mobilized', icon: TruckIcon },
  { value: '412K', label: 'Tons Delivered', icon: Award },
];

// Returns a special-observance banner config when the current date falls in
// Black History Month (February) or the Veterans Day window (Nov 8 – Nov 14).
// Returns the default "Honoring Our Drivers" banner otherwise.
const getObservanceBanner = (now = new Date()) => {
  const month = now.getMonth(); // 0 = Jan
  const day = now.getDate();

  if (month === 1) {
    return {
      key: 'bhm',
      eyebrow: 'Black History Month',
      title: 'Honoring the Drivers Who Drove Patton to Victory',
      copy: '75% of Red Ball Express drivers were African American. We carry their legacy forward — every mile, every load.',
      tone: 'highlight',
    };
  }
  if (month === 10 && day >= 8 && day <= 14) {
    return {
      key: 'veterans',
      eyebrow: 'Veterans Day',
      title: 'Thank You to the Men and Women Behind the Wheel',
      copy: 'From the convoys of 1944 to the highways of today — we salute the veterans and drivers who keep America moving.',
      tone: 'highlight',
    };
  }
  return {
    key: 'default',
    eyebrow: 'Honoring Our Drivers',
    title: 'From 1944 to Today — The Wheel Never Stops.',
    copy: 'We owe our name to the men and women who drove through war, weather and time. Every load is a tribute.',
    tone: 'default',
  };
};

export const HistorySection = () => {
  const banner = getObservanceBanner();
  const isHighlight = banner.tone === 'highlight';
  return (
    <section
      className="relative bg-zinc-950 overflow-hidden py-24 sm:py-32"
      style={{ fontFamily: 'Outfit, sans-serif' }}
      data-testid="home-history-section"
    >
      {/* Top + bottom hairlines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-zinc-800 to-transparent" />

      {/* Faint grid texture */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(90deg, #fff 0, #fff 1px, transparent 1px, transparent 140px), repeating-linear-gradient(0deg, #fff 0, #fff 1px, transparent 1px, transparent 140px)',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* LEFT — Image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="lg:col-span-7 order-2 lg:order-1"
          >
            <div className="relative group">
              {/* Red accent block behind */}
              <div className="absolute -inset-2 sm:-inset-4 bg-red-600/0 border border-red-600/40 translate-x-4 translate-y-4 group-hover:translate-x-5 group-hover:translate-y-5 transition-transform duration-500" />

              <div className="relative overflow-hidden border border-zinc-800">
                <img
                  src="/history-legacy.jpg"
                  alt="Red Ball Express convoy of 1944 transitioning into modern Red Ball Express truck — heritage meets today"
                  className="w-full h-auto object-cover aspect-[16/10] grayscale-[15%] group-hover:grayscale-0 transition-all duration-700"
                  loading="lazy"
                  data-testid="history-legacy-image"
                />

                {/* Cinematic gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/10 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/40 via-transparent to-zinc-950/20" />

                {/* Caption strip */}
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 flex items-end justify-between">
                  <div>
                    <div className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] text-red-500 mb-1">
                      Normandy → Paris · Aug 25 — Nov 16, 1944
                    </div>
                    <div className="text-white text-sm sm:text-base font-semibold leading-tight">
                      The original Red Ball Express convoy.
                    </div>
                  </div>
                  <div className="hidden sm:block text-right">
                    <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">
                      U.S. Army
                    </div>
                    <div className="text-[9px] font-bold uppercase tracking-widest text-zinc-500">
                      Transportation Corps
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* RIGHT — Copy */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.1 }}
            className="lg:col-span-5 order-1 lg:order-2"
          >
            <span className="text-red-500 text-xs font-bold uppercase tracking-[0.25em] mb-4 block">
              Our Heritage · Since 1944
            </span>

            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-[1.02] tracking-tighter mb-6">
              FORGED IN<br />
              WAR.<br />
              <span className="text-red-500">DRIVEN TODAY.</span>
            </h2>

            <div
              className="space-y-5 text-zinc-400 text-base leading-relaxed mb-10"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <p>
                In the summer of 1944, with Allied armies racing across France after D-Day,
                supply lines stretched thin and victory hung on a single thing:{' '}
                <span className="text-white font-semibold">moving freight, fast.</span>
              </p>
              <p>
                The answer was the{' '}
                <span className="text-white font-semibold">Red Ball Express</span> — a
                round-the-clock convoy of nearly 6,000 trucks that delivered{' '}
                <span className="text-white font-semibold">412,000+ tons</span> of fuel,
                ammunition and rations to the front lines in just 83 days. Roughly{' '}
                <span className="text-white font-semibold">75% of its drivers were African
                American</span> — unsung heroes who kept Patton&apos;s Third Army rolling.
              </p>
              <p>
                We carry that name with respect — and the same standard. Every load on time.
                Every mile accounted for. <span className="text-white font-semibold">No
                excuses.</span>
              </p>
            </div>

            {/* Heritage facts */}
            <div className="grid grid-cols-3 gap-3 sm:gap-4 border-t border-zinc-800 pt-8">
              {FACTS.map((f) => {
                const Icon = f.icon;
                return (
                  <div
                    key={f.label}
                    className="group"
                    data-testid={`history-fact-${f.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon className="w-4 h-4 text-red-500 mb-3 transition-transform duration-300 group-hover:-translate-y-0.5" />
                    <div className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-none">
                      {f.value}
                    </div>
                    <div className="text-[10px] sm:text-[11px] font-bold text-zinc-500 uppercase tracking-widest mt-2">
                      {f.label}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Observance / Honoring Our Drivers banner */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className={`mt-8 relative overflow-hidden border ${
                isHighlight
                  ? 'border-red-600/60 bg-gradient-to-r from-red-950/40 via-zinc-900/60 to-zinc-950/60'
                  : 'border-zinc-800 bg-zinc-900/40'
              } px-5 py-4 sm:px-6 sm:py-5`}
              data-testid={`history-observance-banner-${banner.key}`}
            >
              {isHighlight && (
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-600" />
              )}
              <div className="flex items-start gap-3">
                <Heart
                  className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                    isHighlight ? 'text-red-500' : 'text-zinc-500'
                  }`}
                  fill={isHighlight ? 'currentColor' : 'none'}
                />
                <div>
                  <div
                    className={`text-[10px] font-bold uppercase tracking-[0.25em] mb-1 ${
                      isHighlight ? 'text-red-500' : 'text-zinc-500'
                    }`}
                  >
                    {banner.eyebrow}
                  </div>
                  <div
                    className="text-white text-sm sm:text-base font-bold leading-snug mb-1"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {banner.title}
                  </div>
                  <div
                    className="text-zinc-400 text-xs sm:text-sm leading-relaxed"
                    style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                  >
                    {banner.copy}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HistorySection;
